import { useCallback, useEffect, useRef, useState } from "react";
import { MapPin, Navigation, Loader2, X } from "lucide-react";
import { useLabBooking } from "../context/LabBookingContext";

const POPULAR_CITIES = [
  { name: "Mumbai",    lat: 19.076,   lon: 72.8777 },
  { name: "Pune",      lat: 18.5204,  lon: 73.8567 },
  { name: "Delhi",     lat: 28.6139,  lon: 77.209  },
  { name: "Bengaluru", lat: 12.9716,  lon: 77.5946 },
  { name: "Hyderabad", lat: 17.385,   lon: 78.4867 },
  { name: "Chennai",   lat: 13.0827,  lon: 80.2707 },
];

const cityFromAddress = (address = {}) =>
  address.city || address.town || address.suburb || address.county || address.state_district || "";

const LocationModal = ({ onClose }) => {
  const { location, locationStatus, gpsLocation, saveLocation } = useLabBooking();
  const [query, setQuery]         = useState("");
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Reverse-geocode when GPS fires with no city label yet.
  useEffect(() => {
    if (!location || location.city) return;
    fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${location.latitude}&lon=${location.longitude}&format=json`
    )
      .then((r) => r.json())
      .then((data) => {
        const city    = cityFromAddress(data.address);
        const pincode = data.address?.postcode || "";
        saveLocation({ ...location, city, pincode });
      })
      .catch(() => {});
  }, [location, saveLocation]);

  // Debounced Nominatim autocomplete.
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const timer = setTimeout(() => {
      setSearching(true);
      fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          query + ", India"
        )}&format=json&countrycodes=in&limit=6&addressdetails=1`
      )
        .then((r) => r.json())
        .then((data) => setResults(data.slice(0, 5)))
        .catch(() => {})
        .finally(() => setSearching(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  const confirmResult = useCallback(
    (result) => {
      const city    = cityFromAddress(result.address) || result.display_name.split(",")[0].trim();
      const pincode = result.address?.postcode || "";
      saveLocation({ latitude: parseFloat(result.lat), longitude: parseFloat(result.lon), city, pincode });
      onClose();
    },
    [saveLocation, onClose]
  );

  const confirmCity = useCallback(
    (city) => {
      saveLocation({ latitude: city.lat, longitude: city.lon, city: city.name, pincode: "" });
      onClose();
    },
    [saveLocation, onClose]
  );

  const handleGps = () => { gpsLocation(); };

  // Close if GPS granted and city resolved.
  useEffect(() => {
    if (locationStatus === "granted" && location?.city) onClose();
  }, [locationStatus, location, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
          <h2 className="text-[16px] font-extrabold text-[#191C1D]">Select your location</h2>
          <button onClick={onClose} className="text-[#9AA6A4] hover:text-[#191C1D] transition">
            <X size={20} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* GPS button */}
          <button
            onClick={handleGps}
            disabled={locationStatus === "loading"}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-[#13B58E] text-[#13B58E] font-semibold text-[13px] hover:bg-[#EEF9F4] transition disabled:opacity-60"
          >
            {locationStatus === "loading" ? (
              <Loader2 size={18} className="animate-spin shrink-0" />
            ) : (
              <Navigation size={18} className="shrink-0" />
            )}
            {locationStatus === "loading" ? "Detecting your location…" : "Use my current location"}
          </button>

          {locationStatus === "denied" && (
            <p className="text-[11px] text-[#B45309]">
              Location access denied — please search manually below.
            </p>
          )}

          {/* Search input */}
          <div className="relative">
            <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#13B58E]" size={16} />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search city, area or pincode…"
              className="w-full h-11 pl-10 pr-4 rounded-xl border border-gray-200 text-[14px] text-[#191C1D] placeholder:text-[#9AA6A4] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA6A4] hover:text-[#191C1D]"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Search results */}
          {searching && (
            <div className="flex items-center gap-2 text-[13px] text-[#6B7A99] px-1">
              <Loader2 size={13} className="animate-spin" /> Searching…
            </div>
          )}
          {!searching && query.trim() && results.length === 0 && (
            <p className="text-[13px] text-[#6B7A99] px-1">No results for "{query}".</p>
          )}
          {!searching && results.length > 0 && (
            <div className="rounded-xl border border-gray-100 overflow-hidden">
              {results.map((r) => {
                const city   = cityFromAddress(r.address) || r.display_name.split(",")[0].trim();
                const detail = r.display_name.split(",").slice(0, 3).join(",");
                return (
                  <button
                    key={r.place_id}
                    onClick={() => confirmResult(r)}
                    className="w-full flex items-start gap-3 px-4 py-3 border-b border-gray-100 last:border-0 text-left hover:bg-[#F7FAF9] transition"
                  >
                    <MapPin size={14} className="text-[#13B58E] mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-[#191C1D] truncate">{city}</p>
                      <p className="text-[11px] text-[#9AA6A4] truncate">{detail}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Popular cities (shown when no search query) */}
          {!query.trim() && (
            <div>
              <p className="text-[11px] font-semibold text-[#9AA6A4] uppercase tracking-wide mb-2">
                Popular cities
              </p>
              <div className="flex flex-wrap gap-2">
                {POPULAR_CITIES.map((city) => (
                  <button
                    key={city.name}
                    onClick={() => confirmCity(city)}
                    className={`text-[12px] px-3.5 py-1.5 rounded-full border transition font-medium ${
                      location?.city === city.name
                        ? "bg-[#13B58E] text-white border-[#13B58E]"
                        : "border-gray-200 text-[#3D4945] hover:border-[#13B58E] hover:text-[#13B58E]"
                    }`}
                  >
                    {city.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationModal;
